import React, { useEffect, useMemo, useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import { cloneDeep, isEmpty, result } from 'lodash';
import moment from 'moment';
import {
  genarateTableConfigs,
  generateGroupedTableConfigs,
  getGlobalSettings
} from '../utils';
import {
  TransactionDetalisPanel,
  TransactionDetalisPanelTitle,
  TransactionDetailsTable,
  SearchRow,
  MainFiltersRow,
  DownloadButtonWrapper
} from './styled';
import ComponentsMap from '../ComponentsMap';
import { DEFAULT_MAP_NAMESPACE } from './constants';
import {
  DEFAULT_CURRENT_PAGE,
  DEFAULT_PAGE_SIZE
} from '../../constants/commonConstants';
import {
  convertDateInputToEpoch,
  getTimeAdjustment
} from '../../../util/timezone';
import TransactionDetailsFilters from './TransactionDetailsFilters';
import TransactionSearchBar from './TransactionSearchBar';
import DownloadDropdown from './DownloadDropdown';
import AdvancedFiltersPanel from './AdvancedFiltersPanel';
import { Select } from 'antd';

const MIN_SEARCH_LENGTH = 3;

// Sort order toggle: Transaction Date ASC / DESC
const SortOrderToggle = ({ value, onChange }) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginRight: 16, marginLeft: 8 }}>
    <span style={{ fontSize: 11, color: '#748aa1', whiteSpace: 'nowrap' }}>
      Sort Transaction Date:
    </span>
    <Select
      size="small"
      value={value}
      onChange={onChange}
      style={{ width: 90, fontSize: 11 }}
      options={[
        { label: 'Newest', value: 'DESC' },
        { label: 'Oldest', value: 'ASC' }
      ]}
    />
  </div>
);

const buildSearchParams = (query) => {
  const params = {};
  if (query.searchValue && query.searchValue.length >= MIN_SEARCH_LENGTH) {
    params[query.searchField] = query.searchValue;
  }
  return params;
};

const buildAdvancedFilterParams = (query) => {
  const params = {};
  if (query.transactionType) params.transactionType = query.transactionType;
  if (query.accountId) params.accountId = query.accountId;
  if (query.sortOrder) params.sortOrder = query.sortOrder;
  return params;
};

const TransactionDetails = ({
  apiActions,
  alertId,
  isLoading,
  isCustomerInfoLoading,
  isAlertDetailsLoading,
  transactionHistory,
  settings,
  gsQuery,
  typologies,
  customerId,
  mapNamespace = DEFAULT_MAP_NAMESPACE,
  title,
  hasDownloadButton = true,
  hasTriggeredOnlySwitch = true,
  requireTriggeredOnlyParam,
  showWidgetActionLabels = true,
  className,
  alertDetails = {},
  hasTypologiesFilter = true,
  manualCaseDate,
  isSmartAssessment,
  transactionTriggeredVariants,
  isPrevention,
  isTxnHistoryDownloading,
  transactionTypesFilterData,
  isFromCM = false,
  moduleId,
  loginModuleId,
  isManualCase = false,
  module,
  caseId
}) => {
  const { data: alertInfo } = alertDetails;
  const [currentPage, setCurrentPage] = useState(DEFAULT_CURRENT_PAGE);
  const [currentPageSize, setCurrentPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [query, setQuery] = useState({
    isTriggeredOnly: true,
    typologyId: 'all',
    duration: 30,
    searchField: '',
    searchValue: '',
    transactionType: undefined,
    accountId: undefined,
    sortOrder: 'DESC'
  });
  const [advancedFiltersOpen, setAdvancedFiltersOpen] = useState(false);

  const alertDateTO = useMemo(() => {
    if (manualCaseDate) return moment(new Date(manualCaseDate), 'DD/MM/YYYY');
    if (alertInfo?.alertDate) {
      const { hours = '' } = getTimeAdjustment();
      if (hours)
        return moment(new Date(alertInfo?.alertDate), 'DD/MM/YYYY').add(
          hours,
          'hours'
        );

      return moment(new Date(alertInfo?.alertDate), 'DD/MM/YYYY');
    }

    return moment();
  }, [manualCaseDate, alertInfo]);

  const alertDateFROM = useMemo(
    () =>
      cloneDeep(alertDateTO)
        .subtract(query.duration ?? 30, 'days')
        .add(1, 'days'),
    [alertDateTO]
  );

  const {
    getAMLSCommonAlertPartyIdentity,
    getAMLSTMCommonTransactionHistory,
    getAMLSTMCommonTransactionTriggeredTypologies,
    getAMLSTMCommonTransactionTriggeredVariants,
    downloadAMLSTMCommonTransactionHistory,
    getAMLSTMCommonTransactionTypesFilter
  } = apiActions;
  const {
    widgetConfig,
    isSingleLevel = false,
    isVisible
  } = getGlobalSettings({ settings, gsQuery }) || {};

  const getAMLSTMCommonTransactionHistoryFunc = (
    from,
    to,
    triggeredOnly,
    typologyId,
    customerId
  ) => {
    const searchParams = buildSearchParams(query);
    const enhancedFilterParams = buildAdvancedFilterParams(query);

    return getAMLSTMCommonTransactionHistory({
      customerId: customerId ?? undefined,
      alertId,
      limit: currentPageSize,
      offset: (DEFAULT_CURRENT_PAGE - 1) * currentPageSize,
      q: '',
      from: convertDateInputToEpoch(
        from.startOf('day').format('YYYY-MM-DDTHH:mm:ss.SSS[Z]'),
        true
      ),
      to: convertDateInputToEpoch(
        to.endOf('day').format('YYYY-MM-DDTHH:mm:ss.SSS[Z]'),
        true
      ),
      isTriggeredOnly: triggeredOnly || requireTriggeredOnlyParam,
      typologyId: typologyId === 'all' ? undefined : typologyId,
      ...searchParams,
      ...enhancedFilterParams
    });
  };

  useEffect(() => {
    // Skip date calculation and query updates while alert details are still loading
    // This prevents unnecessary state updates
    // For manual cases, alertDetails is not fetched, so skip this guard
    if (isAlertDetailsLoading && !isManualCase) return;

    //The "To" date should not greater than alert Date.
    //So compare the "To" with "Alert Date" when date range only selected(without duration)
    if (query.from && query.to && !query.duration) {
      if (alertDateTO.endOf('day').isBefore(query.to.endOf('day'))) {
        setQuery((value) => ({
          ...value,
          from: alertDateFROM,
          to: alertDateTO,
          duration: 30
        }));
      }
    } else {
      setQuery((value) => ({
        ...value,
        from: alertDateFROM,
        to: alertDateTO,
        duration: value.duration
      }));
    }
  }, [alertDateTO]);

  useEffect(() => {
    // Set the typologyId as 'all' when the alert doesn't contain the selected typology while switching alert.
    if (query?.typologyId !== 'all') {
      if (
        (!isPrevention &&
          typologies?.length &&
          !typologies?.some(({ id }) => id === query?.typologyId)) ||
        (transactionTriggeredVariants?.length &&
          !transactionTriggeredVariants?.some((id) => id === query?.typologyId))
      ) {
        setQuery((value) => ({
          ...value,
          typologyId: 'all'
        }));
      }
    }
  }, [typologies, transactionTriggeredVariants, query?.typologyId]);

  useEffect(() => {
    const { from, to, typologyId, isTriggeredOnly } = query;
    if (
      (isVisible || isManualCase) &&
      from &&
      (manualCaseDate || alertId === alertInfo?.alertId)
    ) {
      const triggeredOnly = hasTriggeredOnlySwitch
        ? isTriggeredOnly
        : undefined;
      if (customerId) {
        getAMLSTMCommonTransactionHistoryFunc(
          from,
          to,
          triggeredOnly,
          typologyId,
          customerId
        );
      }
    }
  }, [customerId, query, isVisible, alertInfo]);

  useEffect(() => {
    if (isManualCase && alertId) {
      getAMLSCommonAlertPartyIdentity({ alertId });
    }
  }, [alertId, isManualCase]);

  useEffect(() => {
    if (hasTypologiesFilter && alertId) {
      if (isPrevention) {
        getAMLSTMCommonTransactionTriggeredVariants({ alertId });
      } else {
        getAMLSTMCommonTransactionTriggeredTypologies({ alertId });
      }
    }
  }, [alertId, hasTypologiesFilter]);

  useEffect(() => {
    if (customerId && getAMLSTMCommonTransactionTypesFilter) {
      getAMLSTMCommonTransactionTypesFilter({
        partyKey: customerId
      });
    }
  }, [customerId]);

  const onChangeWidgetAction = (values) => {
    const triggeredOnly = hasTriggeredOnlySwitch
      ? values.isTriggeredOnly
      : undefined;

    setQuery((prev) => ({
      ...prev,
      ...values,
      isTriggeredOnly: triggeredOnly || requireTriggeredOnlyParam
    }));
    setCurrentPage(DEFAULT_CURRENT_PAGE);
  };

  const onSearchChange = useCallback(({ searchField, searchValue }) => {
    setQuery((prev) => ({ ...prev, searchField, searchValue }));
    setCurrentPage(DEFAULT_CURRENT_PAGE);
  }, []);

  const onAdvancedFiltersApply = useCallback((advancedValues) => {
    setQuery((prev) => ({ ...prev, ...advancedValues }));
    setCurrentPage(DEFAULT_CURRENT_PAGE);
  }, []);

  const handleDownload = ({ downloadAll = false } = {}) => {
    if (!isEmpty(alertId) && isVisible && transactionHistory?.payload?.length) {
      const { from, to, typologyId, isTriggeredOnly } = query;

      const triggeredOnly = hasTriggeredOnlySwitch
        ? isTriggeredOnly
        : undefined;
      const searchParams = buildSearchParams(query);
      const enhancedFilterParams = buildAdvancedFilterParams(query);

      const downloadParams = {
        customerId,
        alertId,
        q: '',
        from: convertDateInputToEpoch(
          from.startOf('day').format('YYYY-MM-DDTHH:mm:ss.SSS[Z]'),
          true
        ),
        to: convertDateInputToEpoch(
          to.endOf('day').format('YYYY-MM-DDTHH:mm:ss.SSS[Z]'),
          true
        ),
        isTriggeredOnly: triggeredOnly || requireTriggeredOnlyParam,
        typologyId: typologyId === 'all' ? undefined : typologyId,
        download: true,
        moduleId: alertInfo?.alertSourceId
          ? Number(alertInfo.alertSourceId)
          : moduleId ?? loginModuleId,
        ...(!downloadAll && {
          offset: (currentPage - 1) * currentPageSize,
          limit: currentPageSize
        }),
        ...(downloadAll && { downloadAll: true, isCM: isFromCM }),
        ...searchParams,
        ...enhancedFilterParams,
        ...(module && { module })
      };

      // Include caseId for all CM_CASE_* and manual case modules
      const isCaseModule = isManualCase || module?.startsWith('CM_CASE_');
      if (isCaseModule && caseId) {
        downloadParams.caseId = caseId;
      }

      downloadAMLSTMCommonTransactionHistory(downloadParams);
    }
  };

  const onDownloadClick = () => handleDownload();
  const onDownloadAllClick = () => handleDownload({ downloadAll: true });

  const onPaginationChange = (page, pageSize) => {
    setCurrentPage(page);
    setCurrentPageSize(pageSize);
    const { from, to, isTriggeredOnly, typologyId } = query;
    if (from && to && !isEmpty(alertId) && isVisible) {
      const searchParams = buildSearchParams(query);
      const enhancedFilterParams = buildAdvancedFilterParams(query);

      getAMLSTMCommonTransactionHistory({
        customerId,
        alertId,
        limit: pageSize,
        offset: (page - 1) * pageSize,
        q: '',
        from: convertDateInputToEpoch(
          from.startOf('day').format('YYYY-MM-DDTHH:mm:ss.SSS[Z]'),
          true
        ),
        to: convertDateInputToEpoch(
          to.endOf('day').format('YYYY-MM-DDTHH:mm:ss.SSS[Z]'),
          true
        ),
        isTriggeredOnly,
        typologyId: typologyId === 'all' ? undefined : typologyId,
        isSmartAssessment,
        ...searchParams,
        ...enhancedFilterParams
      });
    }
  };

  const typologyVariants = useMemo(() => {
    if (!Array.isArray(transactionTriggeredVariants)) return [];

    return [...new Set(transactionTriggeredVariants)].map((d) => ({
      id: d,
      name: d
    }));
  }, [transactionTriggeredVariants]);

  return (
    <TransactionDetalisPanel className={className}>
      {title && (
        <TransactionDetalisPanelTitle style={{ padding: '12px 16px 0' }}>
          {title}
        </TransactionDetalisPanelTitle>
      )}

      <SearchRow>
        <TransactionSearchBar
          onSearch={onSearchChange}
          advancedFiltersOpen={advancedFiltersOpen}
          onToggleAdvancedFilters={setAdvancedFiltersOpen}
          isTMTransaction={!isManualCase}
          advancedFiltersOverlay={
            <AdvancedFiltersPanel
              filters={query}
              onApply={(values) => {
                onAdvancedFiltersApply(values);
                setAdvancedFiltersOpen(false);
              }}
              isTriggeredOnly={query.isTriggeredOnly}
              onClose={() => setAdvancedFiltersOpen(false)}
              typologies={isPrevention ? typologyVariants : typologies || []}
              transactionTypes={transactionTypesFilterData || []}
              isTMTransaction={!isManualCase}
            />
          }
        />
      </SearchRow>

      <MainFiltersRow>
        <TransactionDetailsFilters
          hasTriggeredOnlySwitch={hasTriggeredOnlySwitch}
          showLabels={showWidgetActionLabels}
          filters={query}
          onFilterChange={onChangeWidgetAction}
          alertDateTO={alertDateTO}
        />
        <SortOrderToggle
          value={query.sortOrder || 'DESC'}
          onChange={(order) => {
            setCurrentPage(DEFAULT_CURRENT_PAGE);
            setQuery((prev) => ({ ...prev, sortOrder: order }));
          }}
        />
        {hasDownloadButton && (
          <DownloadButtonWrapper>
            <DownloadDropdown
              onDownloadCurrentPage={onDownloadClick}
              onDownloadAll={onDownloadAllClick}
              isDownloading={isTxnHistoryDownloading}
              isDisabled={
                (transactionHistory?.payload || []).length === 0 || isLoading
              }
              currentPageCount={(transactionHistory?.payload || []).length}
              totalCount={transactionHistory?.total || 0}
            />
          </DownloadButtonWrapper>
        )}
      </MainFiltersRow>

      <TransactionDetailsTable
        dataSource={transactionHistory?.payload || []}
        columns={
          isSingleLevel
            ? genarateTableConfigs({
                settings: widgetConfig,
                configs: result(ComponentsMap, mapNamespace) || {}
              })
            : generateGroupedTableConfigs({
                settings: widgetConfig,
                configs: result(ComponentsMap, mapNamespace) || {}
              })
        }
        loading={isLoading}
        scroll={{ x: 'max-content' }}
        pagination={{
          total: transactionHistory?.total || 0,
          current: currentPage,
          onChange: onPaginationChange,
          pageSize: currentPageSize,
          showTotal: (total) => `${total} Records`,
          showSizeChanger: false
        }}
      />
    </TransactionDetalisPanel>
  );
};

TransactionDetails.propTypes = {
  apiActions: PropTypes.object.isRequired,
  alertId: PropTypes.string,
  transactionHistory: PropTypes.object,
  transactionHistoryDownloaded: PropTypes.object,
  error: PropTypes.object,
  isLoading: PropTypes.bool,
  isCustomerInfoLoading: PropTypes.bool,
  isAlertDetailsLoading: PropTypes.bool,
  settings: PropTypes.object.isRequired,
  gsQuery: PropTypes.object.isRequired,
  typologies: PropTypes.array,
  customerId: PropTypes.string.isRequired,
  mapNamespace: PropTypes.string,
  hasDownloadButton: PropTypes.bool,
  title: PropTypes.string,
  hasTriggeredOnlySwitch: PropTypes.bool,
  requireTriggeredOnlyParam: PropTypes.bool,
  showWidgetActionLabels: PropTypes.bool,
  className: PropTypes.string,
  csvHeaders: PropTypes.object,
  hasTypologiesFilter: PropTypes.bool,
  alertDetails: PropTypes.object,
  manualCaseDate: PropTypes.string,
  isPrevention: PropTypes.bool,
  isTxnHistoryDownloading: PropTypes.bool,
  transactionTypesFilterData: PropTypes.array,
  isManualCase: PropTypes.bool
};

export default TransactionDetails;
